// src/features/apps/components/StoreAccountStep.tsx

import { useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  List,
  Loader,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck, IconMailForward } from '@tabler/icons-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useHelpTokens } from '@/features/help/useHelpTokens';
import {
  FACTORY_STORE_ACCOUNT_STATE_COLORS,
  FACTORY_STORE_ACCOUNT_STATE_LABELS,
  FACTORY_STORE_INFO,
  type FactoryStoreInfo,
  type FactoryStoreKey,
} from '@/lib/constants/appFactory';
import type { AppCfgsDto } from '@/lib/types/App';
import type { FactoryStoreAccount, FactoryStoreDelegate } from '@/lib/types/AppFactory';
import { StoreAccountUtils } from '../StoreAccountUtils';
import { useCreateStoreAccount, useStoreAccounts, useTransitionStoreAccount } from '../useAppFactory';

interface StoreAccountStepProps {
  app: AppCfgsDto;
  /** The stores to set up; default = every store the app onboards. */
  stores?: FactoryStoreKey[];
  onBack?: () => void;
  onNext: () => void;
  nextLabel?: string;
}

const ACCOUNT_LABEL_MAX = 120;

/**
 * Wizard step "Store account" (4.2 delegated store accounts): per store, the
 * customer's developer account and the ONE act it ever performs — inviting
 * the platform's login with the delegated role. Everything after (packages,
 * submissions, updates) rides that delegation and is recorded on the Status
 * tab. A reseller can hold one account for every account it manages. A store
 * published from the operator's own account (Google Play) has no card to fill:
 * one line says so.
 */
export function StoreAccountStep({
  app,
  stores = StoreAccountUtils.storesForApp(app),
  onBack,
  onNext,
  nextLabel = 'Next',
}: StoreAccountStepProps) {
  const { selectedCId, selectedCustomer, isParentAccount } = useAuth();
  const accountsQuery = useStoreAccounts();
  const parentCIds = selectedCustomer?.cfg.parentCId ? [selectedCustomer.cfg.parentCId] : [];

  if (accountsQuery.isLoading) {
    return (
      <Group justify="center" p="md">
        <Loader size="sm" />
      </Group>
    );
  }

  const rows = accountsQuery.data?.storeAccounts ?? [];
  const delegates = accountsQuery.data?.delegates ?? {};
  // The stores with a developer account of the customer's to set up; the rest
  // (Google Play) are published from the operator's own account.
  const delegatedStores = stores.filter((s) => !StoreAccountUtils.isOperatorAccountStore(s));
  const operatorStores = stores.filter((s) => StoreAccountUtils.isOperatorAccountStore(s));

  return (
    <Stack gap="md">
      {delegatedStores.length > 0 && (
        <Text size="sm" c="dimmed">
          Each store publishes apps from a developer account that belongs to you. Set it up once
          per store, invite the login shown below, and that is the last thing the store ever asks of
          you: builds and updates go through that delegation from then on.
        </Text>
      )}

      {operatorStores.map((store) => (
        <OperatorStoreCard key={store} info={FACTORY_STORE_INFO[store]} />
      ))}

      {accountsQuery.isError && (
        <Alert icon={<IconAlertCircle size={16} />} color="gray">
          Store account status is currently unavailable. You can finish this step later from
          the Status tab.
        </Alert>
      )}

      {!accountsQuery.isError &&
        delegatedStores.map((store) => (
          <StoreCard
            key={store}
            info={FACTORY_STORE_INFO[store]}
            account={StoreAccountUtils.resolve(rows, selectedCId ?? 0, parentCIds, store)}
            ownCId={selectedCId ?? 0}
            delegate={delegates[store]}
            isParentAccount={isParentAccount}
          />
        ))}

      <Text size="xs" c="dimmed">
        The channel name, brand and store name are baked into each build ({app.roku?.core.title ??
          app.appCfg.orgInfo.name}
        ) — renaming later means a new build and a new store submission.
      </Text>

      <Group justify="space-between">
        {onBack ? (
          <Button variant="default" onClick={onBack}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <Button onClick={onNext}>{nextLabel}</Button>
      </Group>
    </Stack>
  );
}

// ── A store published from the operator's own developer account (Google Play) ──

function OperatorStoreCard({ info }: { info: FactoryStoreInfo }) {
  const { operatorName } = useHelpTokens();
  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="xs">
        <Group justify="space-between">
          <Text fw={600}>{info.label} publishing</Text>
          <Badge variant="light" color="green">
            Published by {operatorName}
          </Badge>
        </Group>
        <Text size="sm">
          {info.label} apps are published from {operatorName}&apos;s own developer account. There is
          no account to create and nothing to invite: {operatorName} creates the app in the{' '}
          {info.label} console, publishes the first version once the first build passes its checks,
          and publishes every later update for you. Each step shows on the Status tab.
        </Text>
      </Stack>
    </Card>
  );
}

// ── One store ──

interface StoreCardProps {
  info: FactoryStoreInfo;
  account: FactoryStoreAccount | null;
  ownCId: number;
  delegate: FactoryStoreDelegate | undefined;
  isParentAccount: boolean;
}

function StoreCard({ info, account, ownCId, delegate, isParentAccount }: StoreCardProps) {
  const { operatorName } = useHelpTokens();
  const createMutation = useCreateStoreAccount();
  const transitionMutation = useTransitionStoreAccount();
  // Ruling 2: a parent (reseller) account holds one store account covering
  // everything it manages by default; a plain account holds its own.
  const [holderKind, setHolderKind] = useState<'customer' | 'reseller'>(
    isParentAccount ? 'reseller' : 'customer',
  );
  const [diy, setDiy] = useState(false);
  const [accountLabel, setAccountLabel] = useState('');

  const delegateEmail = delegate?.email ?? '';
  const delegateRole = delegate?.role ?? '';
  const inherited = account != null && account.cId !== ownCId;
  const busy = createMutation.isPending || transitionMutation.isPending;

  const fail = (err: Error) =>
    notifications.show({ title: 'Error', message: err.message, color: 'red' });

  const recordInvite = (row: FactoryStoreAccount) =>
    transitionMutation.mutate(
      { appStoreAccountId: row.appStoreAccountId, from: 'pending', to: 'invited' },
      {
        onSuccess: () =>
          notifications.show({
            title: `${info.label} invite recorded`,
            message: 'We activate the account as soon as the delegation lands.',
            color: 'green',
          }),
        onError: fail,
      },
    );

  const handleSentInvite = () => {
    if (account) {
      recordInvite(account);
      return;
    }
    createMutation.mutate(
      {
        store: info.key,
        holderKind,
        coversSubAccounts: holderKind === 'reseller',
        accountLabel: accountLabel.trim(),
        delegateEmail,
        delegateRole,
        submitter: diy ? 'holder' : 'operator',
        ceremonyRunBy: diy ? holderKind : 'operator',
        note: '',
      },
      { onSuccess: (row) => recordInvite(row), onError: fail },
    );
  };

  const handleHolderNext = (row: FactoryStoreAccount, to: string) =>
    transitionMutation.mutate(
      { appStoreAccountId: row.appStoreAccountId, from: row.state, to },
      { onError: fail },
    );

  const stateBadge = account && (
    <Badge variant="light" color={FACTORY_STORE_ACCOUNT_STATE_COLORS[account.state] ?? 'gray'}>
      {FACTORY_STORE_ACCOUNT_STATE_LABELS[account.state] ?? account.state}
    </Badge>
  );

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw={600}>{info.label} developer account</Text>
          {stateBadge}
        </Group>

        {inherited && account && (
          <Text size="sm">
            Covered by the account that manages yours
            {account.accountLabel ? ` (${account.accountLabel})` : ''}. Nothing to do here.
          </Text>
        )}

        {!inherited && (account == null || account.state === 'pending') && (
          <>
            {isParentAccount && !account && (
              <Select
                label="Whose account is it?"
                data={[
                  { value: 'reseller', label: 'One account of mine, covering every account I manage' },
                  { value: 'customer', label: "This customer's own account" },
                ]}
                value={holderKind}
                onChange={(v) => setHolderKind(v === 'customer' ? 'customer' : 'reseller')}
                allowDeselect={false}
              />
            )}
            {!account && (
              <TextInput
                label="Account name in the portal"
                description="Optional — the company or account name as the store shows it, so support can find it"
                maxLength={ACCOUNT_LABEL_MAX}
                value={accountLabel}
                onChange={(e) => setAccountLabel(e.currentTarget.value)}
              />
            )}
            <Stack gap={4}>
              <Text size="sm" fw={600}>
                In the {info.label} portal
              </Text>
              <List size="sm" type="ordered" spacing={4}>
                <List.Item>
                  Sign in at{' '}
                  <Anchor href={info.portalUrl} target="_blank" rel="noopener noreferrer" size="sm">
                    {info.portalUrl.replace('https://', '')}
                  </Anchor>{' '}
                  with the account that owns (or will own) the app. Create the developer account
                  there first if you have none — it must be your organization&apos;s.
                </List.Item>
                <List.Item>
                  Open <b>{info.inviteLocation}</b> and add{' '}
                  <b>{delegateEmail || 'the login shown here once the account service is reachable'}</b>
                  {delegateRole ? (
                    <>
                      {' '}
                      with the role <b>{delegateRole}</b>
                    </>
                  ) : null}
                  .
                </List.Item>
                <List.Item>Send the invite, then click the button below.</List.Item>
              </List>
            </Stack>
            {!account && (
              <Switch
                label="I will submit to the store myself"
                description={`Keep this off and ${operatorName} submits and updates the app for you through the invite. Turn it on to upload packages and record each portal step yourself.`}
                checked={diy}
                onChange={(e) => setDiy(e.currentTarget.checked)}
              />
            )}
            <Group>
              <Button
                leftSection={<IconMailForward size={14} />}
                onClick={handleSentInvite}
                loading={busy}
                disabled={!delegateEmail}
              >
                I sent the invite
              </Button>
            </Group>
          </>
        )}

        {!inherited && account && account.state === 'invited' && (
          <Stack gap="xs">
            <Text size="sm">
              Invite recorded {account.invitedAt ? `on ${account.invitedAt.slice(0, 10)}` : ''}.{' '}
              {account.submitter === 'holder'
                ? 'Once the portal shows the delegation as accepted, mark the account active.'
                : `${operatorName} marks the account active as soon as the delegation lands — nothing more to do.`}
            </Text>
            {StoreAccountUtils.holderAccountNext(account) === 'active' && (
              <Group>
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconCheck size={14} />}
                  loading={busy}
                  onClick={() => handleHolderNext(account, 'active')}
                >
                  The delegation is active
                </Button>
              </Group>
            )}
          </Stack>
        )}

        {!inherited && account && account.state === 'active' && (
          <Text size="sm">
            {account.submitter === 'holder'
              ? 'Active — you submit from the portal and record each step on the Status tab.'
              : `Active — ${operatorName} submits and updates the app for you.`}
          </Text>
        )}

        {/* Who submits is set when the account is created (the switch above); an
            existing row shows the setting read-only — no update route exists yet. */}
        {!inherited && account && account.state !== 'pending' && (
          <SubmitterLine account={account} operatorName={operatorName} />
        )}

        {!inherited && account && account.state === 'action_needed' && (
          <Alert icon={<IconAlertCircle size={16} />} color="orange">
            {account.note || 'The account needs attention before the next submission.'}
            {StoreAccountUtils.holderAccountNext(account) === 'active' && (
              <Group mt="xs">
                <Button size="xs" variant="light" loading={busy} onClick={() => handleHolderNext(account, 'active')}>
                  Resolved — mark active
                </Button>
              </Group>
            )}
          </Alert>
        )}

        {!inherited && account && account.state === 'revoked' && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            The delegation was revoked{account.note ? `: ${account.note}` : ''}. Contact support to
            set the account up again.
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

/** The current "who submits" setting on an existing row, read-only (build spec ring2-followups). */
function SubmitterLine({ account, operatorName }: { account: FactoryStoreAccount; operatorName: string }) {
  return (
    <Text size="xs" c="dimmed">
      {account.submitter === 'holder'
        ? 'You submit portal steps yourself.'
        : `${operatorName} submits for you.`}{' '}
      Changing this later needs support.
    </Text>
  );
}
